import { Component, OnInit } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Inject, PLATFORM_ID } from '@angular/core';

import { UserService } from '../../../../core/services/quiz/user.service';
import { QuizService } from '../../../../core/services/quiz/quiz.service';
import { AppStateService } from '../../../../core/services/quiz/app-state.service';
import { UserStats, Quiz, Category, UserQuizHistoryResponse } from '../../../../core/models';
import { forkJoin } from 'rxjs';
import { getStoredAuthToken } from '../../../../core/auth/auth-token';
import { Footer } from '../../../../layout/footer/footer.component';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule, Footer],
  templateUrl: './dashboard.html',
  styleUrls: ['./dashboard.scss']
})
export class DashboardComponent implements OnInit {
  stats: UserStats | null = null;
  dailyQuiz: Quiz | null = null;
  categories: Category[] = [];
  recentQuizzes: Quiz[] = [];
  quizHistory: UserQuizHistoryResponse | null = null;
  loading = true;
  error = '';
  isAdmin = false;

  genreGradients: Record<string, string> = {
    'the-classics':    'linear-gradient(135deg,#1a0a00,#3d1f00,#0a0500)',
    'sci-fi-odyssey':  'linear-gradient(135deg,#000a1a,#001a3d,#000510)',
    'indie-darlings':  'linear-gradient(135deg,#0a000a,#1a001a,#050005)',
    'horror':          'linear-gradient(135deg,#0d0000,#2a0000,#050000)',
    'world-cinema':    'linear-gradient(135deg,#000d0a,#001a14,#000805)',
    'default':         'linear-gradient(135deg,#0a0a0a,#1a1a1a,#050505)',
  };

  isHistoryModalOpen = false;
  expandedAttemptId: string | null = null;

  openHistoryModal(attemptId?: string): void {
    this.isHistoryModalOpen = true;
    if (attemptId) {
      this.expandedAttemptId = attemptId;
    } else if (this.quizHistory?.history && this.quizHistory.history.length > 0) {
      this.expandedAttemptId = this.quizHistory.history[0].attempt_id || this.quizHistory.history[0]._id || null;
    }
  }

  closeHistoryModal(): void {
    this.isHistoryModalOpen = false;
  }

  toggleAttemptExpand(attemptId: string): void {
    this.expandedAttemptId = this.expandedAttemptId === attemptId ? null : attemptId;
  }

  constructor(
    private userService: UserService,
    private quizService: QuizService,
    private state: AppStateService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.isAdmin = getStoredAuthToken().trim().length > 0;
    }

    const userId = this.state.userId;
    forkJoin({
      stats: this.userService.getStats(userId),
      daily: this.quizService.getDailyQuiz(),
      categories: this.quizService.getCategories(),
      quizzes: this.quizService.getQuizzes(),
      history: this.quizService.getUserQuizHistory(userId),
    }).subscribe({
      next: ({ stats, daily, categories, quizzes, history }) => {
        this.stats = stats;
        this.dailyQuiz = daily?.id ? daily : null;
        this.categories = categories.slice(0, 6);
        this.recentQuizzes = quizzes.quizzes.slice(0, 6);
        this.quizHistory = history;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.error = 'Could not connect to API. Check your backend is running.';
      }
    });
  }

  getGradient(slug: string): string {
    const category = this.categories.find((cat) => cat.id === slug || cat._id === slug);
    const key = category?.slug ?? slug;
    return this.genreGradients[key] ?? this.genreGradients['default'];
  }

  quizIdentifier(quiz: Quiz | null): string {
    if (!quiz) return '';
    return quiz.id || (quiz as { _id?: string })._id || '';
  }

  categoryIdentifier(category: Category | null): string {
    if (!category) return '';
    return category.id || category._id || category.slug || '';
  }

  playDaily(): void {
    const quizId = this.quizIdentifier(this.dailyQuiz);
    if (!quizId) return;

    this.state.setSelectedQuiz(quizId);
    this.router.navigate(['/quiz/play'], { queryParams: { quizId } });
  }

  playQuiz(quizId: string): void {
    if (!quizId) return;

    const directQuiz = this.recentQuizzes.find((quiz) => this.quizIdentifier(quiz) === quizId);
    const directQuizId = this.quizIdentifier(directQuiz ?? null);

    if (directQuizId) {
      this.state.setSelectedQuiz(directQuizId);
      this.router.navigate(['/quiz/play'], { queryParams: { quizId: directQuizId } });
      return;
    }

    this.quizService.getQuizzes(quizId).subscribe({
      next: ({ quizzes }) => {
        const firstQuiz = quizzes[0];
        const firstQuizId = this.quizIdentifier(firstQuiz ?? null);
        if (!firstQuizId) return;
        this.state.setSelectedQuiz(firstQuizId);
        this.router.navigate(['/quiz/play'], { queryParams: { quizId: firstQuizId } });
      }
    });
  }

  scrollToQuizzes(): void {
    document.getElementById('quizzes-section')?.scrollIntoView({ behavior: 'smooth' });
  }

  get accuracy(): string {
    if (this.stats && this.stats.accuracy > 0) return `${this.stats.accuracy}%`;
    if (this.quizHistory && this.quizHistory.overall_accuracy > 0) return `${this.quizHistory.overall_accuracy}%`;
    return this.stats ? `${this.stats.accuracy}%` : '—';
  }

  get rankDisplay(): string {
    if (!this.stats) return '—';
    const r = Math.max(1, this.stats.global_rank || 1);
    return `#${r}`;
  }
}
